import { useEffect, useRef, useState } from "react";
import { CalendarDays } from "lucide-react";

export function Input(props: React.InputHTMLAttributes<HTMLInputElement>) {
  return <input {...props} className={`w-full rounded-md border border-input bg-background px-3 py-2 text-sm outline-none focus:ring-2 focus:ring-ring ${props.className ?? ""}`} />;
}

export function Textarea(props: React.TextareaHTMLAttributes<HTMLTextAreaElement>) {
  return <textarea {...props} className={`w-full rounded-md border border-input bg-background px-3 py-2 text-sm outline-none focus:ring-2 focus:ring-ring ${props.className ?? ""}`} />;
}

export function Select(props: React.SelectHTMLAttributes<HTMLSelectElement>) {
  return <select {...props} className={`w-full rounded-md border border-input bg-background px-3 py-2 text-sm outline-none focus:ring-2 focus:ring-ring ${props.className ?? ""}`} />;
}

export function DateInputVN({
  value, onChange, className, disabled, required, name, id,
}: {
  value: string | null | undefined;
  onChange: (iso: string) => void;
  className?: string;
  disabled?: boolean;
  required?: boolean;
  name?: string;
  id?: string;
}) {
  const isoToDmy = (iso: string | null | undefined): string => {
    if (!iso) return "";
    const m = /^(\d{4})-(\d{2})-(\d{2})/.exec(iso);
    return m ? `${m[3]}/${m[2]}/${m[1]}` : "";
  };
  const dmyToIso = (dmy: string): string | null => {
    const t = dmy.trim();
    if (!t) return "";
    const m = /^(\d{1,2})[\/\-.](\d{1,2})[\/\-.](\d{2}|\d{4})$/.exec(t);
    if (!m) return null;
    let yy = m[3]; if (yy.length === 2) yy = (Number(yy) >= 70 ? "19" : "20") + yy;
    const dd = m[1].padStart(2, "0"); const mm = m[2].padStart(2, "0");
    const d = Number(dd), mo = Number(mm), y = Number(yy);
    if (mo < 1 || mo > 12 || d < 1 || d > 31 || y < 1900 || y > 2999) return null;
    return `${yy}-${mm}-${dd}`;
  };
  const [text, setText] = useState<string>(() => isoToDmy(value));
  const [bad, setBad] = useState(false);
  const lastIso = useRef<string>(value ?? "");
  useEffect(() => {
    if ((value ?? "") !== lastIso.current) {
      lastIso.current = value ?? "";
      setText(isoToDmy(value));
      setBad(false);
    }
  }, [value]);
  const pickerRef = useRef<HTMLInputElement>(null);
  return (
    <span className={`relative inline-flex w-full items-stretch ${className ?? ""}`}>
      <input
        type="text"
        inputMode="numeric"
        placeholder="dd/mm/yyyy"
        value={text}
        disabled={disabled}
        required={required}
        name={name}
        id={id}
        onChange={(e) => {
          const v = e.target.value;
          setText(v);
          if (v.trim() === "") { setBad(false); lastIso.current = ""; onChange(""); return; }
          const iso = dmyToIso(v);
          if (iso !== null) { setBad(false); lastIso.current = iso; onChange(iso); }
          else { setBad(true); }
        }}
        onBlur={() => {
          if (text.trim() === "") { setBad(false); return; }
          const iso = dmyToIso(text);
          if (iso === null) setBad(true);
          else { setBad(false); setText(isoToDmy(iso)); }
        }}
        className={`w-full rounded-md border bg-background px-3 py-2 pr-9 text-sm outline-none focus:ring-2 focus:ring-ring ${bad ? "border-destructive" : "border-input"}`}
      />
      <button
        type="button"
        tabIndex={-1}
        disabled={disabled}
        onClick={() => {
          const el = pickerRef.current;
          if (!el) return;
          if (value) el.value = value;
          (el as any).showPicker?.();
          el.click();
        }}
        className="absolute right-0 top-0 inline-flex h-full items-center justify-center px-2 text-muted-foreground hover:text-foreground"
        title="Chọn ngày từ lịch"
      >
        <CalendarDays className="h-4 w-4" />
      </button>
      <input
        ref={pickerRef}
        type="date"
        tabIndex={-1}
        aria-hidden
        defaultValue={value ?? ""}
        onChange={(e) => {
          const iso = e.target.value || "";
          lastIso.current = iso;
          setText(isoToDmy(iso));
          setBad(false);
          onChange(iso);
        }}
        style={{ position: "absolute", inset: 0, opacity: 0, pointerEvents: "none", width: "100%", height: "100%" }}
      />
    </span>
  );
}
